// biome-ignore lint/style/noRestrictedImports: encapsulation
import { Root, Thumb } from "@radix-ui/react-switch";
import { type VariantProps, cv } from "@suse-o11y/class-variance";
import { type IgnorePasswordManagers, ignorePasswordManagerAttributes } from "@suse-o11y/ignore-password-managers";
import { mergeRefs } from "@suse-o11y/merge-refs";
import { useComputed } from "@suse-o11y/signal";
import { useOnInvalidFormFocus } from "@suse-o11y/signal/form";
import React from "react";

import { EventGate } from "~/components/event-gate";
import { useFormContext } from "~/components/form/use-form-context";
import { useFormFieldContext } from "~/components/form/use-form-field-context";

import styles from "./_switch.module.css";

/**
 * Check out Figma for the design of the switch:
 *
 * @link https://www.figma.com/design/YIkZms7ygvgqXYnbzDFr8Y/--StackState-DS?node-id=4386-2857&t=GOwABcsH9SrSgyGQ-4
 */
const variants = cv(styles.switch, {
	variants: {
		size: {
			sm: styles.sm,
			md: styles.md,
			lg: styles.lg,
		},
	},

	defaultVariants: {
		size: "md",
	},
});

export interface SwitchProps
	extends IgnorePasswordManagers,
		VariantProps<typeof variants>,
		Omit<React.ComponentProps<typeof Root>, "onMouseDown" | "onMouseUp" | "children"> {
	loading?: boolean;
}

export const Switch = React.forwardRef<React.ElementRef<typeof Root>, SwitchProps>(
	({ allowPasswordManager, className, size, disabled, loading, checked, defaultChecked, onBlur, ...props }, ref) => {
		const form = useFormContext();
		const formField = useFormFieldContext();
		const focusRef = useOnInvalidFormFocus(formField?.state);
		const submitting = useComputed((monitor) => formField?.state.isSubmitting(monitor), [formField?.state]);

		return (
			<EventGate<typeof Root>
				aria-busy={submitting || loading}
				aria-disabled={disabled}
				checked={checked}
				className={variants({ size, className })}
				defaultChecked={defaultChecked}
				form={form?.id}
				id={formField?.id}
				listening={!(disabled || loading || submitting)}
				onBlur={(event) => {
					formField?.state.setTouched(true);
					onBlur?.(event);
				}}
				// prevent focus-within from clicking
				onMouseDown={(event) => event.preventDefault()}
				onMouseUp={(event) => event.preventDefault()}
				ref={mergeRefs([ref, focusRef])}
				{...ignorePasswordManagerAttributes(allowPasswordManager)}
				{...props}
			>
				<Root>
					<Thumb className={styles.thumb} />
				</Root>
			</EventGate>
		);
	},
);

if (import.meta.env.DEV) {
	Switch.displayName = "Switch";
}
